import React, { useEffect, useRef, useState } from "react";
import { useApiIsLoaded } from "@vis.gl/react-google-maps";
import { Button } from "./ui/button";
import { MapPin, Home } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface IProps {
  svLocation: IStreetViewLocation;
  /** Show street names on the panorama */
  addressControl?: boolean;
  /** Show zoom control buttons */
  zoomControl?: boolean;
  /** Show pan control */
  panControl?: boolean;
  /** Show fullscreen control */
  fullscreenControl?: boolean;
  /** Enable navigation arrows for moving through connected panoramas */
  linksControl?: boolean;
  /** Show close button */
  enableCloseButton?: boolean;
  /** Callback when user explicitly updates the location */
  onUpdate: (location: IStreetViewLocation) => void;
  /** Whether update button should be disabled */
  disableUpdate?: boolean;
}

interface ICoverageVersion {
  panoId: string;
  date: Date;
  displayDate: string;
}

export const StreetViewPanorama: React.FC<IProps> = ({
  svLocation,
  addressControl = false,
  zoomControl = true,
  panControl = false,
  fullscreenControl = true,
  linksControl = true,
  enableCloseButton = false,
  onUpdate,
  disableUpdate = false,
}) => {
  const panoramaRef = useRef<HTMLDivElement>(null);
  const streetViewRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const apiIsLoaded = useApiIsLoaded();
  const [coverageVersions, setCoverageVersions] = useState<ICoverageVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);


  const handleUpdate = () => {
    if (!streetViewRef.current) return;

    const position = streetViewRef.current.getPosition();
    const pov = streetViewRef.current.getPov();
    const zoom = streetViewRef.current.getZoom();
    const panoId = streetViewRef.current.getPano();

    if (!position) return;

    const updatedLocation: IStreetViewLocation = {
      lat: position.lat(),
      lng: position.lng(),
      heading: pov.heading,
      pitch: pov.pitch,
      zoom: zoom,
      panoId: panoId || null,
    };

    onUpdate(updatedLocation);
  };

  const handleHome = () => {
    if (!streetViewRef.current) return;

    streetViewRef.current.setPosition({
      lat: svLocation.lat,
      lng: svLocation.lng,
    });

    streetViewRef.current.setPov({
      heading: svLocation.heading,
      pitch: svLocation.pitch,
    });

    streetViewRef.current.setZoom(svLocation.zoom);
  };

  const fetchAvailableCoverage = async () => {
    if (!streetViewRef.current || !google?.maps?.StreetViewService) return;

    const streetViewService = new google.maps.StreetViewService();
    const currentPano = streetViewRef.current.getPano();
    if (!currentPano) return;

    try {
      // Fetch panorama data by pano ID to get the time machine data for this specific location
      const response = await streetViewService.getPanorama({
        pano: currentPano,
      });

      if (response?.data) {
        // Access time machine data (undocumented API - data structure: {nA: Date, pano: string})
        // @ts-ignore
        const timeMachine = response.data.time;

        if (timeMachine && Array.isArray(timeMachine)) {
          const versions: ICoverageVersion[] = timeMachine.map((item: any) => {
            // item.fA is an ISO 8601 date string (e.g., "2015-07-01T05:00:00.000Z")
            //
            //
            //Google keeps changing the date field
            const fields = Object.keys(item);
            let date = undefined;

            if (fields.includes("eA")) {
              date = item.eA;
            } else if (fields.includes("fa")) {
              date = item.fA;
            }

            return {
              panoId: item.pano,
              date: date,
              displayDate: formatDate(date),
            };
          });

          // Sort by date (newest first)
          versions.sort((a, b) => b.date.getTime() - a.date.getTime());

          setCoverageVersions(versions);

          // Sync the selected version with the current pano being displayed
          if (versions.some(v => v.panoId === currentPano)) {
            setSelectedVersion(currentPano);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching coverage versions:", error);
    }
  };

  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = date.toLocaleString("default", { month: "short" });
    return `${month} ${year}`;
  };

  const handleVersionChange = (panoId: string) => {
    if (!streetViewRef.current) return;

    setSelectedVersion(panoId);
    streetViewRef.current.setPano(panoId);
  };

  useEffect(() => {
    if (!panoramaRef.current || !apiIsLoaded) return;
    if (!google || !google.maps || !google.maps.StreetViewPanorama) return;


    const panorama = new google.maps.StreetViewPanorama(panoramaRef.current, {
      position: { lat: svLocation.lat || 0, lng: svLocation.lng || 0 },
      pov: { heading: svLocation.heading, pitch: svLocation.pitch },
      zoom: svLocation.zoom,
      addressControl,
      zoomControl,
      panControl,
      fullscreenControl,
      linksControl,
      enableCloseButton,
      motionTracking: false,
      motionTrackingControl: false,
      imageDateControl: true,
    });

    streetViewRef.current = panorama;

    // Listen for position changes to refresh coverage versions
    const positionListener = panorama.addListener("position_changed", () => {
      fetchAvailableCoverage();
    });

    // Listen for pano changes to update the selected version in the dropdown
    const panoListener = panorama.addListener("pano_changed", () => {
      const currentPano = panorama.getPano();
      if (currentPano) {
        setSelectedVersion(currentPano);
      }
    });

    return () => {
      google.maps.event.removeListener(positionListener);
      google.maps.event.removeListener(panoListener);
    };
  }, [apiIsLoaded]);

  // Update panorama position when svLocation changes (e.g., from map clicks)
  useEffect(() => {
    if (!streetViewRef.current) return;

    // Prefer panoId over lat/lng if available
    if (svLocation.panoId) {
      streetViewRef.current.setPano(svLocation.panoId);
    } else {
      streetViewRef.current.setPosition({
        lat: svLocation.lat,
        lng: svLocation.lng,
      });
    }

    streetViewRef.current.setPov({
      heading: svLocation.heading,
      pitch: svLocation.pitch,
    });

    streetViewRef.current.setZoom(svLocation.zoom);
  }, [
    svLocation.lat,
    svLocation.lng,
    svLocation.panoId
  ]);

  // Fetch available coverage versions when location changes
  useEffect(() => {
    if (!streetViewRef.current || !apiIsLoaded) return;

    const panorama = streetViewRef.current;

    // Listen for when panorama data is loaded
    const listener = panorama.addListener("status_changed", () => {
      const status = panorama.getStatus();
      if (status === "OK") {
        // Panorama loaded successfully, fetch coverage data
        fetchAvailableCoverage();
      }
    });

    // Also try to fetch immediately if already loaded
    const timer = setTimeout(() => {
      fetchAvailableCoverage();
    }, 1000);

    return () => {
      google.maps.event.removeListener(listener);
      clearTimeout(timer);
    };
  }, [svLocation.lat, svLocation.lng, svLocation.panoId, apiIsLoaded]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <div
        ref={panoramaRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "16px",
          transform: "translateY(-50%)",
          zIndex: 10,
        }}
      >
        <TooltipProvider>
          <div className="flex flex-col gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleHome}
                  size="sm"
                  className="w-10 h-10 p-0"
                  variant="outline"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: 'black' }}
                >
                  <Home className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="font-mono">Reset to Saved Location</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleUpdate}
                  disabled={disableUpdate}
                  size="sm"
                  className="w-10 h-10 p-0"
                  variant="outline"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: 'black' }}
                >
                  <MapPin className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="font-mono">Update Location</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
      {coverageVersions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "16px",
            left: "16px",
            zIndex: 10,
          }}
        >
          <Select value={selectedVersion || undefined} onValueChange={handleVersionChange}>
            <SelectTrigger
              size="sm"
              className="w-[140px] font-mono [&_svg]:!text-black [&_svg]:!opacity-100"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)', color: 'black' }}
            >
              <SelectValue placeholder="Select date" />
            </SelectTrigger>
            <SelectContent align="start" className="font-mono">
              {coverageVersions.map((version) => (
                <SelectItem key={version.panoId} value={version.panoId}>
                  {version.displayDate}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default StreetViewPanorama;
